import React, { useEffect, useState } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import DbService from "../services/DbService";
import { Chip, Stack } from "@mui/material";
import ActionsCell from "./actionsCell";
import { enqueueSnackbar } from "notistack";
import { School } from "../types/School";
import {
  createSchool,
  deleteSchool,
  updateSchool,
} from "../services/schoolService";
import EmailAvatar from "./EmailAvatar";
import { useAdmin } from "../context/AdminContext";
import { DynamicTableListTemplate } from "./shared/DynamicTableListTemplate";
import { useHandleError } from "../hooks/useHandleError";
import SchoolPrincipalCombinedForm from "./forms/SchoolPrincipalCombinedForm";

const SchoolList: React.FC = () => {
  const handleError = useHandleError();

  const [loading, setLoading] = React.useState(false);
  const [selectedSchool, setSelectedSchool] = useState<School | undefined>(
    undefined
  );
  const [schoolToDelete, setSchoolToDelete] = useState<School | null>(null);

  const [hasSchoolsChanged, setHasSchoolsChanged] = useState<boolean>(false);

  const hasFetchedRef = React.useRef(false);

  const { schools, setSchoolsToAdminProvider, refreshSchoolAndPrincipalList } =
    useAdmin();

  const columns: GridColDef[] = [
    {
      field: "isActive",
      headerName: "Status",
      width: 90,
      renderCell: (params) => {
        const color = params.value === true ? "success" : "default";
        const text = params.value === true ? "ACTIVE" : "DISABLED";
        return (
          <Chip
            style={{ fontSize: ".7rem" }}
            label={text}
            size="small"
            color={color}
          />
        );
      },
    },
    { field: "name", headerName: "School Name", width: 250 },
    {
      field: "city",
      headerName: "City",
      width: 130,
      valueGetter: (params) => {
        return params.row?.city;
      },
    },
    {
      field: "country",
      headerName: "Country",
      width: 130,
      valueGetter: (params) => {
        return params.row?.country;
      },
    },
    { field: "principalName", headerName: "Principal Name", width: 200 },

    {
      field: "subscribedPrograms",
      headerName: "Subscribed Programs",
      width: 205,
      renderCell: (params) => {
        const chipData = [
          { label: "YELLOW", value: "EAP_YELLOW", color: "#f2b443" },
          { label: "GREEN", value: "EAP_GREEN", color: "#78c0a1" },
          { label: "ACCESS", value: "EAP_ACCESS", color: "#34c1d6" },
        ];

        return (
          <Stack direction="row" spacing={-3}>
            {Array.isArray(params.value) &&
              chipData.map((chip) =>
                params.value.includes(chip.value) ? (
                  <Chip
                    key={chip.label}
                    style={{ fontSize: ".7rem", backgroundColor: chip.color }}
                    label={chip.label}
                    size="small"
                  />
                ) : null
              )}
          </Stack>
        );
      },
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return;
        }
        const updatedBy = params.row["updatedBy"];
        const date = new Date(params.value);
        // Format the date using the user's local computer locale
        const formattedDate = date.toLocaleDateString("en-AU");

        return (
          <div
            style={{
              display: "flex",
              gap: ".25rem",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{formattedDate}</span>
            <EmailAvatar email={updatedBy}></EmailAvatar>
          </div>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row: School = params.row as School;

        return (
          <ActionsCell
            onEdit={() => handleEditSchool(row)}
            onDelete={() => handleDeleteSchool(row)}
          />
        );
      },
    },
  ];

  useEffect(() => {
    const fetchSchools = async () => {
      setLoading(true);
      try {
        const response = await DbService.getSchools();
        setSchoolsToAdminProvider(response.data);
      } catch (err: any) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetchedRef.current) {
      fetchSchools();
      hasFetchedRef.current = true;
    }
  }, [handleError, setSchoolsToAdminProvider]);

  const handleCloseDialog = () => {
    setSelectedSchool(undefined);
    if (hasSchoolsChanged) {
      refreshList();
    }
  };

  const handleCreateSchool = async (schoolData: School) => {
    if (schoolData._id) {
      handleUpdateSchool(schoolData);
    } else {
      try {
        const response = await createSchool(schoolData);

        enqueueSnackbar(response.message, {
          variant: "success",
        });

        setSelectedSchool(response.data);
        setHasSchoolsChanged(true);
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleUpdateSchool = async (updatedSchoolData: School) => {
    if (!updatedSchoolData._id) {
      return;
    }
    try {
      const response = await updateSchool(
        updatedSchoolData._id,
        updatedSchoolData
      );

      setSelectedSchool(updatedSchoolData);

      enqueueSnackbar(response.message, {
        variant: "success",
      });
      setHasSchoolsChanged(true);
    } catch (error) {
      handleError(error);
    }
  };

  const handleEditSchool = (row: School) => {
    setSelectedSchool(row);
  };

  const handleDeleteSchool = async (row: School) => {
    if (!row?._id) {
      enqueueSnackbar(`Id is missing from this ${row.name}`, {
        variant: "error",
      });
      return;
    }
    setSchoolToDelete(row);
  };

  const handleDeleteSchoolConfirm = async (row: School | null | undefined) => {
    if (row?._id) {
      try {
        const response = await deleteSchool(row._id);
        enqueueSnackbar(response.message, {
          variant: "success",
        });
        refreshList();
      } catch (error) {
        handleError(error);
      } finally {
        setSchoolToDelete(null);
      }
    }

    setSchoolToDelete(null);
  };

  const refreshList = async () => {
    setLoading(true);
    try {
      await refreshSchoolAndPrincipalList();
    } catch (err: any) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DynamicTableListTemplate<School>
      columns={columns}
      data={schools}
      loading={loading}
      selectedRow={selectedSchool}
      FormComponent={
        <SchoolPrincipalCombinedForm
          selectedRow={selectedSchool}
          onSubmit={handleCreateSchool}
        />
      }
      entityLabel="School"
      dialogTitle="School Entry Form"
      onClearSelectedRow={handleCloseDialog}
      onConfirmDelete={handleDeleteSchoolConfirm}
      entityToDelete={schoolToDelete}
    />
  );
};
export default SchoolList;
